import { ArrowLeftOutlined, ReloadOutlined } from "@ant-design/icons";
import { ThreadAnalysisDefItem, ThreadAnalysisResultItem } from "@coeff/api";
import {
  Breadcrumb,
  Button,
  Checkbox,
  Drawer,
  Input,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from "antd";
import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import styled from "styled-components";

import { useThreadAnalyses, useThreadAnalysisResults } from "../../../api";
import { AppState } from "../../../store";

import { FindingsList, hasTurnAnchors } from "./FindingsList";
import { ResultView } from "./ResultView";
import { ThreadTranscript } from "./ThreadTranscript";

const { Text } = Typography;

// Results are loaded in one window (most-recent first) and searched/sorted client-side. If an
// analysis has more than this, the tail isn't loaded — the toolbar says so rather than implying
// the full set is present.
const RESULTS_LIMIT = 1000;

/**
 * `?date=YYYY-MM-DD` narrows results to that UTC day, matching how the daily digest buckets a
 * thread (on analyzed_through_dt). The digest's "Explore today's analysis" link lands here.
 */
function dayWindow(date?: string): { since?: string; until?: string } {
  if (!date || !/^\d{4}-\d{2}-\d{2}$/.test(date)) {
    return {};
  }
  const start = new Date(`${date}T00:00:00Z`);
  if (Number.isNaN(start.getTime())) {
    return {};
  }
  const end = new Date(start.getTime() + 24 * 60 * 60 * 1000);
  return { since: start.toISOString(), until: end.toISOString() };
}

const PageWrapper = styled.div`
  padding: 24px;
  max-width: 1400px;
`;

// Each pane scrolls on its own, so the findings stay put while the transcript moves under them.
const DrawerColumns = styled.div`
  display: flex;
  height: 100%;
  min-height: 0;
`;

const FindingsPane = styled.div`
  flex: 0 0 40%;
  overflow-y: auto;
  padding: 20px 16px 20px 24px;
  border-right: 1px solid #eee;
  background: #fafafa;
`;

const TranscriptPane = styled.div`
  flex: 1 1 60%;
  overflow-y: auto;
  padding: 20px 24px 20px 16px;
`;

const PageTitle = styled.div`
  font-size: 20px;
  font-weight: 600;
  color: #1a1a1a;
  margin-bottom: 4px;
`;

const PageSubtitle = styled.div`
  font-size: 13px;
  color: #888;
  margin-bottom: 16px;
`;

const Toolbar = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
  margin-bottom: 12px;
`;

const DefinitionCard = styled.div`
  padding: 10px 14px;
  margin-bottom: 12px;
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 6px;
`;

const DefinitionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  flex-wrap: wrap;
`;

const DefinitionField = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const FieldLabel = styled.span`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #999;
`;

const SummaryBar = styled.div`
  display: flex;
  gap: 20px;
  align-items: baseline;
  flex-wrap: wrap;
  padding: 10px 14px;
  margin-bottom: 12px;
  background: #f6f8fa;
  border: 1px solid #e8e8e8;
  border-radius: 6px;
`;

const SummaryStat = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const StatValue = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: #1a1a1a;
`;

const StatLabel = styled.div`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #999;
`;

const PromptBlock = styled.pre`
  margin: 0;
  padding: 10px 12px;
  background: #f6f8fa;
  border: 1px solid #e8e8e8;
  border-radius: 4px;
  color: #333;
  font-size: 12px;
  max-height: 320px;
  overflow: auto;
  white-space: pre-wrap;
  word-break: break-word;
`;

function formatDateTime(iso?: string): string {
  return iso ? new Date(iso).toLocaleString() : "-";
}

// Items across the result's top-level arrays — a cheap, analysis-agnostic size, computed from the
// raw result the API returns (no server-side computation).
function resultItemCount(result?: ThreadAnalysisResultItem["result"]): number {
  if (!result) {
    return 0;
  }
  return Object.values(result).reduce<number>((n, v) => n + (Array.isArray(v) ? v.length : 0), 0);
}

export const ThreadAnalysis: React.FC = () => {
  const { isCoeffAdmin } = useSelector(({ app }: AppState) => ({
    isCoeffAdmin: app.isCoeffAdmin,
  }));

  const [selected, setSelected] = useState<ThreadAnalysisDefItem | undefined>();
  const [showPrompt, setShowPrompt] = useState(false);
  const [keyword, setKeyword] = useState("");
  const [findingsOnly, setFindingsOnly] = useState(false);
  const [openThread, setOpenThread] = useState<ThreadAnalysisResultItem | undefined>();
  const [focusRequest, setFocusRequest] = useState<
    { turn?: number; quote?: string; nonce: number } | undefined
  >();

  const location = useLocation();
  const urlParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const [dateFilter, setDateFilter] = useState<string | undefined>(
    () => urlParams.get("date") ?? undefined
  );

  const analysesQuery = useThreadAnalyses();
  const analyses = analysesQuery.data ?? [];

  // Deep link from the daily digest: ?analysis=<name|id>&date=YYYY-MM-DD lands straight in that
  // analysis's results instead of the analyses list. Only applied while nothing is selected, so
  // it never yanks the view out from under someone who has since navigated.
  const analysisParam = urlParams.get("analysis");
  useEffect(() => {
    if (selected || !analysisParam || !analyses.length) {
      return;
    }
    const match = analyses.find(a => a.name === analysisParam || a.analysis_id === analysisParam);
    if (match) {
      setSelected(match);
    }
  }, [analysisParam, analyses, selected]);

  const resultsQuery = useThreadAnalysisResults(selected?.analysis_id, {
    limit: RESULTS_LIMIT,
    offset: 0,
    ...dayWindow(dateFilter),
  });

  const results = resultsQuery.data?.results ?? [];
  const total = resultsQuery.data?.total ?? 0;

  useEffect(() => {
    if (analysesQuery.isError) {
      message.error("Failed to load analyses");
    }
  }, [analysesQuery.isError]);

  useEffect(() => {
    if (resultsQuery.isError) {
      message.error("Failed to load results");
    }
  }, [resultsQuery.isError]);

  // Precompute one lowercased search string per row, recomputed only when the loaded rows change
  // (serializing the result JSON is the expensive part — don't redo it on every keystroke).
  const rowHaystacks = useMemo(
    () =>
      results.map(r =>
        `${r.email || ""} ${r.domain || ""} ${r.thread_id} ${JSON.stringify(
          r.result || {}
        )}`.toLowerCase()
      ),
    [results]
  );

  // Loaded-window summary: how many threads produced findings and how many items in total.
  const summary = useMemo(() => {
    let withFindings = 0;
    let items = 0;
    results.forEach(r => {
      const n = resultItemCount(r.result);
      if (n > 0) {
        withFindings += 1;
      }
      items += n;
    });
    return { withFindings, items };
  }, [results]);

  // Client-side filters over the loaded rows: findings-only + every keyword term must appear in
  // the row's haystack.
  const filteredResults = useMemo(() => {
    const terms = keyword.toLowerCase().split(/\s+/).filter(Boolean);
    return results.filter((r, i) => {
      if (findingsOnly && resultItemCount(r.result) === 0) {
        return false;
      }
      return terms.every(t => rowHaystacks[i].includes(t));
    });
  }, [results, keyword, findingsOnly, rowHaystacks]);

  if (!isCoeffAdmin) {
    return <div>Unauthorized</div>;
  }

  const analysisColumns = [
    {
      title: "Analysis",
      key: "name",
      render: (_: unknown, r: ThreadAnalysisDefItem) => (
        <div>
          <div style={{ fontWeight: 500 }}>{r.name}</div>
          <Text style={{ fontFamily: "monospace", fontSize: 11, color: "#999" }}>
            {r.model || "—"}
          </Text>
        </div>
      ),
    },
    {
      title: "Threads analyzed",
      dataIndex: "thread_count",
      key: "thread_count",
      width: 160,
      sorter: (a: ThreadAnalysisDefItem, b: ThreadAnalysisDefItem) =>
        a.thread_count - b.thread_count,
      render: (v: number) => <Text>{v.toLocaleString()}</Text>,
    },
    {
      title: "Last analyzed",
      dataIndex: "last_analyzed_dt",
      key: "last_analyzed_dt",
      width: 200,
      render: (v: string | undefined) => (
        <Text style={{ fontSize: 12, color: "#888" }}>{formatDateTime(v)}</Text>
      ),
    },
  ];

  const resultColumns = [
    {
      title: "Owner",
      key: "email",
      width: 260,
      sorter: (a: ThreadAnalysisResultItem, b: ThreadAnalysisResultItem) =>
        (a.email || "").localeCompare(b.email || ""),
      render: (_: unknown, r: ThreadAnalysisResultItem) => (
        <div>
          <div style={{ fontSize: 13 }}>{r.email || "—"}</div>
          <Text style={{ fontSize: 11, color: "#999" }}>{r.domain || ""}</Text>
        </div>
      ),
    },
    {
      title: "Result items",
      key: "result_count",
      width: 120,
      defaultSortOrder: "descend" as const,
      sorter: (a: ThreadAnalysisResultItem, b: ThreadAnalysisResultItem) =>
        resultItemCount(a.result) - resultItemCount(b.result),
      render: (_: unknown, r: ThreadAnalysisResultItem) => {
        const n = resultItemCount(r.result);
        return (
          <Tag color={n ? "blue" : "default"}>{n ? `${n} item${n === 1 ? "" : "s"}` : "empty"}</Tag>
        );
      },
    },
    {
      title: "Turns",
      dataIndex: "turns_analyzed",
      key: "turns_analyzed",
      width: 90,
      sorter: (a: ThreadAnalysisResultItem, b: ThreadAnalysisResultItem) =>
        (a.turns_analyzed || 0) - (b.turns_analyzed || 0),
      render: (v: number | undefined) => <Text style={{ fontSize: 12 }}>{v ?? "—"}</Text>,
    },
    {
      title: "Thread",
      dataIndex: "thread_id",
      key: "thread_id",
      width: 140,
      render: (id: string) => (
        <Text
          copyable={{ text: id }}
          style={{ fontFamily: "monospace", fontSize: 11, color: "#888" }}
        >
          {id.slice(0, 8)}…
        </Text>
      ),
    },
    {
      title: "Analyzed",
      dataIndex: "analyzed_through_dt",
      key: "analyzed_through_dt",
      width: 180,
      sorter: (a: ThreadAnalysisResultItem, b: ThreadAnalysisResultItem) =>
        new Date(a.analyzed_through_dt || 0).getTime() -
        new Date(b.analyzed_through_dt || 0).getTime(),
      render: (v: string | undefined) => (
        <Text style={{ fontSize: 12, color: "#888" }}>{formatDateTime(v)}</Text>
      ),
    },
    {
      title: "",
      key: "actions",
      width: 90,
      render: (_: unknown, r: ThreadAnalysisResultItem) => (
        <Button size="small" onClick={() => setOpenThread(r)}>
          View
        </Button>
      ),
    },
  ];

  return (
    <PageWrapper>
      <PageTitle>Thread Analysis</PageTitle>
      <PageSubtitle>
        Browse prompt-driven analyses of agent threads. Pick an analysis, drill into analyzed
        threads, and read the full transcript alongside its result.
      </PageSubtitle>

      {!selected ? (
        <Table
          dataSource={analyses}
          columns={analysisColumns}
          rowKey="analysis_id"
          loading={analysesQuery.isLoading}
          size="small"
          pagination={false}
          onRow={r => ({ onClick: () => setSelected(r), style: { cursor: "pointer" } })}
          locale={{ emptyText: "No analyses defined" }}
        />
      ) : (
        <>
          <Breadcrumb style={{ marginBottom: 12 }}>
            <Breadcrumb.Item>
              <a onClick={() => setSelected(undefined)}>Analyses</a>
            </Breadcrumb.Item>
            <Breadcrumb.Item>{selected.name}</Breadcrumb.Item>
          </Breadcrumb>

          <DefinitionCard>
            <DefinitionRow>
              <Text strong style={{ fontSize: 13 }}>
                Definition
              </Text>
              <DefinitionField>
                <FieldLabel>Model</FieldLabel>
                <Text code>{selected.model || "—"}</Text>
              </DefinitionField>
              <DefinitionField>
                <FieldLabel>Tool calls</FieldLabel>
                <Tag color={selected.include_tool_calls ? "blue" : "default"}>
                  {selected.include_tool_calls ? "on" : "off"}
                </Tag>
              </DefinitionField>
              <Button
                type="link"
                size="small"
                style={{ marginLeft: "auto", padding: 0 }}
                onClick={() => setShowPrompt(v => !v)}
              >
                {showPrompt ? "Hide prompt" : "Show prompt"}
              </Button>
            </DefinitionRow>
            {showPrompt && (
              <PromptBlock style={{ marginTop: 10 }}>
                {selected.prompt || "(no prompt stored)"}
              </PromptBlock>
            )}
          </DefinitionCard>

          <SummaryBar>
            <SummaryStat>
              <StatValue>{summary.withFindings.toLocaleString()}</StatValue>
              <StatLabel>Threads with findings</StatLabel>
            </SummaryStat>
            <SummaryStat>
              <StatValue>{summary.items.toLocaleString()}</StatValue>
              <StatLabel>Total result items</StatLabel>
            </SummaryStat>
            <SummaryStat>
              <StatValue>{results.length.toLocaleString()}</StatValue>
              <StatLabel>
                Threads loaded
                {total > results.length ? ` of ${total.toLocaleString()}` : ""}
              </StatLabel>
            </SummaryStat>
            {total > results.length && (
              <Text type="warning" style={{ fontSize: 12 }}>
                Showing the most recent {results.length.toLocaleString()} threads — older ones
                aren&apos;t loaded.
              </Text>
            )}
          </SummaryBar>

          <Toolbar>
            <Button icon={<ArrowLeftOutlined />} onClick={() => setSelected(undefined)}>
              Back
            </Button>
            <Input.Search
              placeholder="Filter by keywords (owner, thread id, result text)"
              value={keyword}
              onChange={e => setKeyword(e.target.value)}
              allowClear
              style={{ width: 360 }}
            />
            <Checkbox checked={findingsOnly} onChange={e => setFindingsOnly(e.target.checked)}>
              Findings only
            </Checkbox>
            {/* Arriving from a digest link narrows to one day — say so, and make it undoable. */}
            {dateFilter && (
              <Tag closable color="blue" onClose={() => setDateFilter(undefined)}>
                {dateFilter} (UTC)
              </Tag>
            )}
            <Button icon={<ReloadOutlined />} onClick={() => resultsQuery.refetch()} type="text" />
            <Text type="secondary" style={{ fontSize: 13 }}>
              {resultsQuery.isFetching
                ? "Loading…"
                : `${filteredResults.length.toLocaleString()} shown`}
            </Text>
          </Toolbar>

          <Table
            dataSource={filteredResults}
            columns={resultColumns}
            rowKey="thread_id"
            loading={resultsQuery.isFetching}
            size="small"
            pagination={{ pageSize: 25, showSizeChanger: false, hideOnSinglePage: true }}
            onRow={r => ({ onClick: () => setOpenThread(r), style: { cursor: "pointer" } })}
            locale={{ emptyText: "No threads found" }}
          />
        </>
      )}

      <Drawer
        title={
          openThread ? (
            <Tooltip title={openThread.thread_id}>
              <span style={{ fontSize: 14 }}>
                Thread {openThread.thread_id.slice(0, 8)}… · {openThread.email || "—"}
              </span>
            </Tooltip>
          ) : (
            "Thread"
          )
        }
        width={1280}
        visible={!!openThread}
        onClose={() => {
          setOpenThread(undefined);
          setFocusRequest(undefined);
        }}
        destroyOnClose
        bodyStyle={{ padding: 0, overflow: "hidden" }}
      >
        {openThread &&
          // Findings beside the transcript, not stacked above it: clicking a finding scrolls the
          // transcript to the turn it came from, and the surrounding messages stay in view.
          // Analyses whose prompt emits no turn anchors keep the generic result table.
          (hasTurnAnchors(openThread.result) ? (
            <DrawerColumns>
              <FindingsPane>
                <Text strong style={{ fontSize: 13 }}>
                  Findings
                </Text>
                <div style={{ marginTop: 8 }}>
                  <FindingsList
                    result={openThread.result}
                    onJump={anchor => setFocusRequest({ ...anchor, nonce: Date.now() })}
                  />
                </div>
              </FindingsPane>
              <TranscriptPane>
                <ThreadTranscript threadId={openThread.thread_id} focusRequest={focusRequest} />
              </TranscriptPane>
            </DrawerColumns>
          ) : (
            <div style={{ padding: 24 }}>
              <Text strong style={{ fontSize: 13 }}>
                Analysis result
              </Text>
              <div style={{ marginTop: 8, marginBottom: 20 }}>
                <ResultView result={openThread.result} />
              </div>
              <div style={{ borderTop: "1px solid #eee", paddingTop: 16 }}>
                <ThreadTranscript threadId={openThread.thread_id} />
              </div>
            </div>
          ))}
      </Drawer>
    </PageWrapper>
  );
};
